import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { LicenseService } from "../../services/licenseService";
import logger from "../../core/utils/logger";

export const fetchLicenseStatus = createAsyncThunk(
    "license/fetchStatus",
    async (_, { rejectWithValue }) => {
        try {
            const response = await LicenseService.getLicenseStatus();
            return response;
        } catch (error) {
            logger.error("[Redux - fetchLicenseStatus] Error:", error);
            return rejectWithValue(error.response?.data || { message: "Error fetching license status" });
        }
    }
);

export const activateLicense = createAsyncThunk(
    "license/activate",
    async (licenseKey, { rejectWithValue }) => {
        try {
            const response = await LicenseService.activateLicense(licenseKey);
            return response;
        } catch (error) {
            logger.error("[Redux - activateLicense] Error:", error);
            const status = error.response?.status;
            if (status === 400 && error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ reason: "Network", message: error.message || "Error activating license" });
        }
    }
);

const initialState = {
    status: null,
    loading: false,
    error: null,
    // Tracks the requestId of the most recently STARTED fetchLicenseStatus.
    // A later-starting fetch always wins; older fulfilled actions are ignored
    // so the mount-fetch can't overwrite a fresher post-402 refresh.
    latestFetchRequestId: null,
};

const licenseSlice = createSlice({
    name: "license",
    initialState,
    reducers: {
        clearLicenseError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLicenseStatus.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                state.latestFetchRequestId = action.meta.requestId;
            })
            .addCase(fetchLicenseStatus.fulfilled, (state, action) => {
                if (state.latestFetchRequestId && action.meta.requestId !== state.latestFetchRequestId) {
                    return;
                }
                state.loading = false;
                state.error = null;
                if (action.payload) {
                    state.status = action.payload;
                }
            })
            .addCase(fetchLicenseStatus.rejected, (state, action) => {
                if (state.latestFetchRequestId && action.meta.requestId !== state.latestFetchRequestId) {
                    return;
                }
                state.loading = false;
                state.error = action.payload?.message || "Error fetching license status";
            })
            .addCase(activateLicense.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(activateLicense.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                if (action.payload) {
                    state.status = action.payload;
                }
            })
            .addCase(activateLicense.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearLicenseError } = licenseSlice.actions;
export default licenseSlice.reducer;
